/**
 * Who this campaign's audience would ring right now, and why not the rest
 * (plan section 4.5: "N would be rung, M left out by reason").
 *
 * Asked from the server a moment after the filters stop changing: the same
 * read, lists and screen the queue uses, so the number here is the number a
 * refill acts on. The two numbers must add up (callable + every refusal =
 * examined); a preview that does not is said out loud rather than trusted.
 *
 * The angle on screen goes with the request (ring R9): an alert campaign counts
 * only companies with a defect worth a call right now, and never clients.
 */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, Smartphone } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  audienceKey,
  previewAiCampaignAudience,
  previewBalances,
  skipRows,
  skippedTotal,
  warnsNoneHeard,
  type CampaignAudience,
  type CampaignAngle,
  type CampaignAudiencePreview,
  type CampaignKind,
} from "../../../../lib/aiCampaigns";
import { formatPhone } from "../../../../lib/phone";
import { fmtNum } from "../../../ui";
import { Panel } from "../../marketing/shared";
import { Note } from "../../settings/panel/fields";

/** How long the filters must stay still before the preview is asked for. */
const PREVIEW_DELAY_MS = 600;

export function AudiencePreviewCard({
  audience,
  kind,
  angle,
  campaignId,
  enabled,
}: {
  audience: CampaignAudience;
  /** A warm offer campaign whose callable companies never heard from us is worth a warning. */
  kind: CampaignKind;
  /** The angle on screen, saved or not: an alert audience is read differently. */
  angle: CampaignAngle;
  /** Leaves out companies this campaign already queued. */
  campaignId?: string;
  /** Only a manager may ask (the route is `manage:crm.calls`). */
  enabled: boolean;
}) {
  const t = useT();
  const [preview, setPreview] = useState<CampaignAudiencePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [shownKey, setShownKey] = useState("");
  const request = useRef(0);
  const key = audienceKey(audience);

  useEffect(() => {
    if (!enabled) return;
    const id = ++request.current;
    setLoading(true);
    const timer = setTimeout(() => {
      previewAiCampaignAudience(audience, angle, campaignId)
        .then((next) => {
          if (id !== request.current) return;
          setPreview(next);
          setShownKey(`${angle}:${key}`);
          setFailed(false);
        })
        .catch(() => {
          if (id === request.current) setFailed(true);
        })
        .finally(() => {
          if (id === request.current) setLoading(false);
        });
    }, PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
    // `key` stands for the audience: the preview depends on nothing else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, angle, campaignId, enabled]);

  if (!enabled) return null;

  const stale = preview !== null && shownKey !== `${angle}:${key}`;
  const rows = preview ? skipRows(preview.skipped) : [];

  return (
    <Panel
      title={t("aiCalls.campaigns.preview.title")}
      description={t(`aiCalls.campaigns.preview.unit.${preview?.unit ?? (audience.source === "brain" ? "domain" : "company")}`)}
      actions={loading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
    >
      {failed && !preview ? (
        <Note tone="bad">{t("aiCalls.campaigns.preview.failed")}</Note>
      ) : !preview ? (
        <p className="crm-sub text-sm">{t("aiCalls.campaigns.preview.computing")}</p>
      ) : (
        <div className={stale ? "space-y-4 opacity-60" : "space-y-4"}>
          <div>
            <p className="text-[28px] font-semibold leading-none tabular-nums">{fmtNum(preview.callable)}</p>
            <p className="crm-sub mt-1 text-sm">
              {t("aiCalls.campaigns.preview.callable", { skipped: fmtNum(skippedTotal(preview.skipped)), examined: fmtNum(preview.examined) })}
            </p>
            {angle === "alert" ? <p className="crm-sub mt-1 text-xs">{t("aiCalls.campaigns.preview.alertOnly")}</p> : null}
            {preview.partial ? (
              <p className="crm-sub mt-1 text-xs">
                {t("aiCalls.campaigns.preview.partial", { examined: fmtNum(preview.examined), total: fmtNum(preview.total) })}
              </p>
            ) : null}
          </div>

          {!previewBalances(preview) ? (
            <Note tone="bad">{t("aiCalls.campaigns.preview.unbalanced")}</Note>
          ) : null}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="crm-lab !mt-0">{t("aiCalls.campaigns.preview.mobiles")}</p>
              <p className="tabular-nums">{fmtNum(preview.mobiles)}</p>
            </div>
            <div>
              <p className="crm-lab !mt-0">{t("aiCalls.campaigns.preview.heardFromUs")}</p>
              <p className="tabular-nums">{fmtNum(preview.heardFromUs)}</p>
            </div>
          </div>

          <div>
            <p className="crm-lab !mt-0">{t("aiCalls.campaigns.preview.whyNot")}</p>
            {rows.length === 0 ? (
              <p className="crm-sub text-sm">{t("aiCalls.campaigns.preview.nothingLeftOut")}</p>
            ) : (
              <ul className="space-y-1.5">
                {rows.map((row) => (
                  <li key={row.reason} className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="min-w-0">
                      {t(`aiCalls.campaigns.skip.${row.reason}`)}
                      <span className="crm-sub block text-xs">{t(`aiCalls.campaigns.skipWhy.${row.reason}`)}</span>
                    </span>
                    <span className="shrink-0 tabular-nums">{fmtNum(row.count)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {preview.sample.length > 0 ? (
            <div>
              <p className="crm-lab !mt-0">{t("aiCalls.campaigns.preview.sample")}</p>
              <ul className="divide-y divide-border text-sm">
                {preview.sample.map((row) => (
                  <li key={row.key} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 py-1.5">
                    <span className="min-w-0 truncate">{row.companyName || row.domain}</span>
                    <span className="crm-sub flex items-center gap-1 text-xs tabular-nums">
                      {row.mobile ? <Smartphone className="size-3" aria-label={t("aiCalls.campaigns.preview.mobile")} /> : null}
                      {formatPhone(row.phone)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {warnsNoneHeard(kind, angle, preview) ? (
            <p className="crm-sub flex items-start gap-1.5 text-xs">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              {t("aiCalls.campaigns.preview.noneHeard")}
            </p>
          ) : null}
        </div>
      )}
    </Panel>
  );
}
